import { useRef, useEffect } from "react";
import Slider from "react-slick";
import * as styles from "../styles/MyDashboard.module.scss";
import { useTileData } from "../hooks/useTileData";
import { usePersonnelData } from "../hooks/usePersonnelData";
import { ITile } from "../services/TileService";
import TileTag from "./TileTag";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MSAL imports
import { useMsal } from "@azure/msal-react";

// Material-UI imports
import { theme } from "../styles/theme";
import { Constants } from "../Constants";
import { CircularProgress, FormControlLabel, Switch, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useMemo, useState } from "react";
import { useProfileData } from "../hooks/useProfileData";
import { useNavUrls } from "../hooks/useNavUrls";

type ProfileRoutingData = {
  directorate?: string;
  stream?: string;
} | null;

type ServiceAreaRoute = {
  directorate: "Corporate" | "Finance, IT & Property" | "Seniors" | "Communities";
  value: string;
  label?: string;
  aliases: string[];
};

const serviceAreaRoutes: ServiceAreaRoute[] = [
  { directorate: "Corporate", value: "Finance", aliases: ["Finance"] },
  { directorate: "Finance, IT & Property", value: "Digital Technology", aliases: ["Digital Technology", "DigitalTechnology"] },
  { directorate: "Corporate", value: "HR & Payroll", aliases: ["HR & Payroll", "HR and Payroll", "HRPayroll"] },
  { directorate: "Corporate", value: "Property", aliases: ["Property"] },
  {
    directorate: "Seniors",
    value: "Home and Community Care",
    aliases: ["Home and Community Care", "Home & Community Care", "HACC"]
  },
  {
    directorate: "Seniors",
    value: "Residential Aged Care",
    aliases: ["Residential Aged Care", "RAC"]
  },
  {
    directorate: "Seniors",
    value: "Retirement Living (ILU)",
    aliases: ["Retirement Living (ILU)", "Retirement Living ILU", "Retirement Living"]
  },
  {
    directorate: "Seniors",
    value: "SAHF (Affordable Housing)",
    aliases: ["SAHF (Affordable Housing)", "SAHF Affordable Housing", "Affordable Housing", "SAHF"]
  },
  {
    directorate: "Communities",
    value: "Mental Health & Wellbeing",
    aliases: ["Mental Health & Wellbeing", "Mental Health and Wellbeing", "Mental Health"]
  },
  { directorate: "Communities", value: "Early Learning", aliases: ["Early Learning"] },
  {
    directorate: "Communities",
    value: "Permanency Support Program (PSP)",
    aliases: ["Permanency Support Program (PSP)", "Permanency Support Program", "PSP"]
  },
  {
    directorate: "Communities",
    value: "Intensive Family Services (IFS)",
    aliases: ["Intensive Family Services (IFS)", "Intensive Family Services", "IFS"]
  },
  {
    directorate: "Communities",
    value: "Local Area Coordination (LAC)",
    aliases: ["Local Area Coordination (LAC)", "Local Area Coordination", "LAC", "Directorate Communities"]
  },
  {
    directorate: "Communities",
    value: "Youth Transition (YT)",
    aliases: ["Youth Transition (YT)", "Youth Transition", "YT"]
  },
  {
    directorate: "Communities",
    value: "Counselling & Mediation",
    aliases: ["Counselling & Mediation", "Counselling and Mediation", "Counselling"]
  },
  { directorate: "Communities", value: "Homelessness", aliases: ["Homelessness"] },
  { directorate: "Communities", value: "Disability", aliases: ["Disability"] },
];

const normalizeRoutingValue = (value: string) =>
  value
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getMatchedServiceAreaRoute = (value: string) => {
  const normalizedValue = normalizeRoutingValue(value);

  return serviceAreaRoutes.find((route) =>
    route.aliases.some((alias) => {
      const normalizedAlias = normalizeRoutingValue(alias);
      return normalizedValue === normalizedAlias || normalizedValue.includes(normalizedAlias);
    })
  );
};

const collectStringValues = (source: unknown, visited = new Set<object>()): string[] => {
  if (typeof source === "string") {
    return [source.trim()].filter(Boolean);
  }

  if (!source || typeof source !== "object") {
    return [];
  }

  if (visited.has(source as object)) {
    return [];
  }

  visited.add(source as object);

  if (Array.isArray(source)) {
    return source.flatMap((item: unknown) => collectStringValues(item, visited));
  }

  return Object.values(source).flatMap((value: unknown) => collectStringValues(value, visited));
};

const getFirstString = (source: Record<string, unknown> | null | undefined, keys: string[]) => {
  if (!source) {
    return "";
  }

  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const resolveServiceArea = (personnelData: any, profileData: ProfileRoutingData) => {
  const topLevelServiceArea = getFirstString(personnelData, [
    "serviceArea",
    "servicearea",
    "service_area",
    "serviceAreaName",
    "service_area_name",
    "programArea",
    "program_area",
  ]);

  if (topLevelServiceArea) {
    return topLevelServiceArea;
  }

  if (Array.isArray(personnelData?.careSystems)) {
    for (const careSystem of personnelData.careSystems) {
      const careSystemServiceArea = getFirstString(careSystem, [
        "serviceArea",
        "servicearea",
        "service_area",
        "serviceAreaName",
        "service_area_name",
      ]);

      if (careSystemServiceArea) {
        return careSystemServiceArea;
      }
    }
  }

  if (typeof profileData?.stream === "string" && profileData.stream.trim()) {
    return profileData.stream.trim();
  }

  if (typeof profileData?.directorate === "string" && profileData.directorate.trim() === "Residential Aged Care") {
    return "Residential Aged Care";
  }

  const matchedRouteFromPayload = collectStringValues(personnelData)
    .map((value: string) => getMatchedServiceAreaRoute(value))
    .find(Boolean);

  if (matchedRouteFromPayload) {
    return matchedRouteFromPayload.value;
  }

  return "";
};

const resolveTileRoutingContext = (profileData: ProfileRoutingData, personnelData: any) => {
  const profileDirectorate = typeof profileData?.directorate === "string" ? profileData.directorate.trim() : "";
  const serviceArea = resolveServiceArea(personnelData, profileData);
  const matchedRoute = serviceArea ? getMatchedServiceAreaRoute(serviceArea) : undefined;
  const directorate = matchedRoute?.directorate ?? profileDirectorate;

  if (!directorate || !serviceArea) {
    return {
      directorate: "",
      serviceArea: "",
    };
  }

  return {
    directorate,
    serviceArea,
  };
};

const MyDashboard = () => {
  const { instance, inProgress } = useMsal();
  const navUrls = useNavUrls(inProgress, instance);
  const isIPhone = /iPhone/i.test(navigator.userAgent);
  const lacSourceUrl = navUrls?.find(navUrl => navUrl.key === "ai-chat")?.url;
  const lacExternalUrl = useMemo(() => {
    if (!isIPhone || !lacSourceUrl) return "";

    try {
      const url = new URL(lacSourceUrl, window.location.origin);
      const hashParams = new URLSearchParams(url.hash.slice(1));
      hashParams.set("flow", "LAC");
      hashParams.delete("embedded");
      url.hash = hashParams.toString();
      return url.toString();
    } catch {
      return "";
    }
  }, [isIPhone, lacSourceUrl]);
  const showDevToggles = process.env.REACT_APP_SHOW_DEV_TOGGLES === "true";
  const [forceRacc, setForceRacc] = useState(false);
  const [forceDirectorate, setForceDirectorate] = useState("");
  const [forceServiceArea, setForceServiceArea] = useState("");
  const { profileData } = useProfileData(inProgress, instance);
  const { personnelData } = usePersonnelData(inProgress, instance);
  const routingContext = useMemo(
    () => resolveTileRoutingContext(profileData as ProfileRoutingData, personnelData),
    [personnelData, profileData]
  );
  const directorateOptions = {
    Corporate: serviceAreaRoutes.filter((route) => route.directorate === "Corporate").map((route) => route.value),
    Seniors: serviceAreaRoutes.filter((route) => route.directorate === "Seniors").map((route) => route.value),
    Communities: serviceAreaRoutes.filter((route) => route.directorate === "Communities").map((route) => route.value)
  } as const;

  const toggleForceRacc = (checked: boolean) => {
    setForceRacc(checked);
    if (checked) {
      setForceDirectorate("");
      setForceServiceArea("");
    }
  };

  const toggleForceDirectorate = (value: string) => {
    const nextValue = forceDirectorate === value ? "" : value;
    setForceDirectorate(nextValue);
    if (nextValue) {
      setForceRacc(false);
      setForceServiceArea("");
    } else {
      setForceServiceArea("");
    }
  };

  const effectiveForceRacc = forceRacc && !forceDirectorate;
  const effectiveDirectorate = forceDirectorate || routingContext.directorate;
  const effectiveServiceArea = forceDirectorate
    ? forceServiceArea
    : (effectiveDirectorate ? routingContext.serviceArea : "");
  
  const { tileData, isLoading } = useTileData(
    inProgress,
    instance,
    effectiveForceRacc,
    effectiveDirectorate,
    effectiveServiceArea
  );
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("all");
  const sliderRef = useRef<any>(null);
  

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  }, [activeTab]);

  function NextArrow(props: any) {
    const { className, style, onClick } = props;
    return (
      <div
        className={`${className} ${styles.sliderArrow}`}
        style={{ ...style, right: "8px" }}
        onClick={onClick}
      />
    );
  }

  function PrevArrow(props: any) {
    const { className, style, onClick } = props;
    return (
      <div
        className={`${className} ${styles.sliderArrow}`}
        style={{ ...style, left: "4px" }}
        onClick={onClick}
      />
    );
  }

  /// Function to handle click events on tiles
  /// This function will open the tile's webUrl in a new tab if the app is not installed
  /// and will handle the blur event to clear the timeout if the window is blurred.
  let timeoutId: NodeJS.Timeout | null = null;
  function onclick(item: ITile) {
    if (isMobile && item.appUrl && !item.appUrl.startsWith("http")) {      
      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener("blur", cleanup);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
          cleanup();
        }
      };

      window.addEventListener("blur", cleanup);
      document.addEventListener("visibilitychange", handleVisibilityChange);

      timeoutId = setTimeout(() => {
        console.log("Fallback: opening web URL for item:", item.webUrl);
        window.open(item.webUrl, "_blank"); // Fallback URL if the app is not installed
      }, 500); // Delay to allow the click event to propagate
    }
  }

  // Tab filtering logic
  let displayedTiles: ITile[] = tileData?.tiles ?? [];

  // Hide daily-huddle tile if no care system has dailyHuddleAccess
  const hasDailyHuddleAccess = personnelData?.careSystems?.some(
    (cs: any) => cs.dailyHuddleAccess === true
  );
  if (!hasDailyHuddleAccess) {
    displayedTiles = displayedTiles.filter((item) => item.name !== "daily-huddle");
  }

  if (activeTab === "care") {
    displayedTiles = displayedTiles.filter((item) =>
      item.category?.includes("care")
    );
  } else if (activeTab === "admin") {
    displayedTiles = displayedTiles.filter((item) =>
      item.category?.includes("admin")
    );
  }

  // Adjust placeholder content based on displayed tiles
  const placeholderContent = [];
  const remainder = displayedTiles.length % 9;
  const itemsNeeded = remainder === 0 ? 0 : 9 - remainder;

  if (itemsNeeded > 0) {
    for (let i = 0; i < itemsNeeded; i++) {
      placeholderContent.push(
        <Box
          className={`placeholder ${styles.sliderItemContainer}`}
          key={`my_dashboard_placeholder_${i}`}
        >
          <Box
            className={styles.sliderItem}
            sx={{
              borderBottom: 1,
              borderRight: 1,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            }}
          />
        </Box>
      );
    }
  }

  const isSingleSlide = displayedTiles.length <= 9;
  const settings = {
    dots: true,
    infinite: !isSingleSlide,
    slidesToShow: 1,
    slidesToScroll: 1,
    slidesPerRow: 3,
    speed: 500,
    rows: 3,
    arrows: isMobile ? false : true,
    dotsClass: `slick-dots ${styles.sliderDots}`,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const tabConfig = [
    { label: t("dashboard_tab_all"), value: "all" },
    { label: t("dashboard_tab_care"), value: "care" },
    { label: t("dashboard_tab_admin"), value: "admin" },
  ];

  return (
    <Box className={styles.MyDashboard}>
      <Typography variant="h5">
        {t(Constants.translationKeys.my_dashboard_title)}
      </Typography>

      {isLoading && <CircularProgress color="primary" />}

      {!isLoading && tileData && (
        <>
        {showDevToggles && (
          <Box sx={{ marginBottom: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
            {(profileData as { directorate?: string } | null)?.directorate !== "Residential Aged Care" && (
              <FormControlLabel
                control={
                  <Switch
                    checked={forceRacc}
                    onChange={e => toggleForceRacc(e.target.checked)}
                  />
                }
                label="RAC tiles"
              />
            )}

            {[
              { label: "Corporate", value: "Corporate" },
              { label: "Seniors", value: "Seniors" },
              { label: "Communities", value: "Communities" }
            ].map((directorate) => (
              <FormControlLabel
                key={directorate.value}
                control={
                  <Switch
                    checked={forceDirectorate === directorate.value}
                    onChange={() => toggleForceDirectorate(directorate.value)}
                  />
                }
                label={directorate.label}
              />
            ))}

            {forceDirectorate && (
              <Box sx={{ minWidth: 220 }}>
                <label style={{ display: "block", marginBottom: 6, fontSize: 12, fontWeight: 600 }}>
                  Service Area
                </label>
                <select
                  value={forceServiceArea}
                  onChange={(e) => setForceServiceArea(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    background: "white"
                  }}
                >
                  <option value="">Select a service area</option>
                  {(directorateOptions[forceDirectorate as keyof typeof directorateOptions] ?? []).map((area) => {
                    const value = typeof area === "string" ? area : area.value;
                    const label = typeof area === "string" ? area : area.label;
                    return <option key={value} value={value}>{label}</option>;
                  })}
                </select>
              </Box>
            )}
          </Box>
        )}

        {tileData && tileData.tabsEnabled && (
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
            boxShadow: 1,
            borderRadius: "8px"
          }}
        >
          <Tabs
            value={activeTab}
            variant="fullWidth"
            onChange={(event, newValue) => setActiveTab(newValue)}
            aria-label="tiles category tabs"
          >
            {tabConfig.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                disableRipple
              />
            ))}
          </Tabs>
          </Box>
        )}

        <Box
          className={styles.sliderContainer}
          sx={{
            backgroundColor: theme.palette.common.white,
            boxShadow: 1,
          }}
        >
          <Slider ref={sliderRef} {...settings} className={styles.slider}>
            {displayedTiles.map((item: ITile, index: number) => {
              return (
                <Box
                  component={Link}
                  key={`my_dashboard_tile_${index}`}
                  variant="tile"
                  href={item.name === "lac-ai-agent" ? lacExternalUrl || undefined : item.name === "daily-huddle" ? undefined : (isMobile && item.appUrl ? item.appUrl : item.webUrl)}
                  target={
                    item.name !== "lac-ai-agent" && isMobile && item.appUrl && !item.appUrl.startsWith("http")
                      ? "_self"
                      : "_blank"
                  }
                  rel="noreferrer"
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    if (item.name === "daily-huddle") {
                      e.preventDefault();
                      navigate(Constants.navigation.urls.dailyHuddleFacilities);
                      return;
                    }

                    if (item.name === "lac-ai-agent") {
                      if (isIPhone && lacExternalUrl) return;
                      e.preventDefault();
                      navigate(Constants.navigation.urls.lacAiAgent);
                      return;
                    }

                    onclick(item);
                  }}
                  className={styles.sliderItemContainer}
                  data-parentid="my-dashboard"
                  data-id={`my_dashboard_tile_${item.name}`}
                >
                  <Box
                    className={styles.sliderItem}
                    sx={{
                      borderBottom: 1,
                      borderRight: 1,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    <img
                      src={`/icons/tile-icons/${item.name}.svg`}
                      alt={item.title}
                    />
                    <Typography variant="date" className={styles.itemTitle}>
                      {t(`my_dashboard_tile_${item.name}`, { defaultValue: item.title })}
                    </Typography>
                    {item.tags &&
                      item.tags.map((tag) => {
                        return (
                          <TileTag
                            tag={tag}
                            label={t(`my_dashboard_tile_tag_${tag}`)}
                            key={`my_dashboard_tile_tag_${index}`}
                          />
                        );
                      })}
                  </Box>
                </Box>
              );
            })}
            {placeholderContent}
          </Slider>
        </Box>
        </>
      )}
    </Box>
  );
};

export default MyDashboard;
